/**
 * Chart color palettes and semantic status mappings.
 *
 * CSS variable references (var(--chart-1)) for Nivo/Recharts theme integration.
 * Hex fallbacks for contexts that don't support CSS variables (canvas, export).
 */

// ---------------------------------------------------------------------------
// Chart Palettes
// ---------------------------------------------------------------------------

export interface ColorPalette {
	id: string;
	name: string;
	colors: string[];
}

export const chartPalettes: ColorPalette[] = [
	{
		id: "default",
		name: "Domyslna",
		colors: ["#165c3a", "#267a4f", "#3a9966", "#2d8065", "#35917a"],
	},
	{
		id: "ocean",
		name: "Ocean",
		colors: ["#0891b2", "#0e7490", "#155e75", "#164e63", "#083344"],
	},
	{
		id: "forest",
		name: "Las",
		colors: ["#15803d", "#166534", "#14532d", "#4d7c0f", "#365314"],
	},
	{
		id: "sunset",
		name: "Zachod slonca",
		colors: ["#ea580c", "#dc2626", "#be123c", "#9333ea", "#4f46e5"],
	},
	{
		id: "pastel",
		name: "Pastelowa",
		colors: ["#86efac", "#93c5fd", "#fca5a5", "#fde047", "#c4b5fd"],
	},
	{
		id: "corporate",
		name: "Korporacyjna",
		colors: ["#1e40af", "#374151", "#0f766e", "#b91c1c", "#6b21a8"],
	},
	{
		id: "vibrant",
		name: "Intensywna",
		colors: ["#f43f5e", "#8b5cf6", "#06b6d4", "#84cc16", "#f97316"],
	},
	{
		id: "monochrome",
		name: "Monochromatyczna",
		colors: ["#18181b", "#3f3f46", "#71717a", "#a1a1aa", "#d4d4d8"],
	},
] as const;

export const defaultChartColors = chartPalettes[0].colors;

// ---------------------------------------------------------------------------
// CSS Variable References (for components that support CSS vars)
// ---------------------------------------------------------------------------

export const chartCssVars = [
	"var(--chart-1)",
	"var(--chart-2)",
	"var(--chart-3)",
	"var(--chart-4)",
	"var(--chart-5)",
] as const;

export const trendCssVars = {
	positive: "var(--positive)",
	negative: "var(--negative)",
} as const;

// ---------------------------------------------------------------------------
// Semantic Status Colors
// ---------------------------------------------------------------------------

export type StatusVariant = "success" | "warning" | "error" | "info";

export interface StatusColors {
	bg: string;
	text: string;
	border: string;
}

export const statusCssVars: Record<StatusVariant, StatusColors> = {
	success: {
		bg: "var(--status-success-bg)",
		text: "var(--status-success-text)",
		border: "var(--status-success-border)",
	},
	warning: {
		bg: "var(--status-warning-bg)",
		text: "var(--status-warning-text)",
		border: "var(--status-warning-border)",
	},
	error: {
		bg: "var(--status-error-bg)",
		text: "var(--status-error-text)",
		border: "var(--status-error-border)",
	},
	info: {
		bg: "var(--status-info-bg)",
		text: "var(--status-info-text)",
		border: "var(--status-info-border)",
	},
};

/**
 * Tailwind class mappings for status variants.
 * Use these in Badge, Alert, and other status-driven components
 * instead of hardcoding text-blue-600, bg-green-500, etc.
 */
export const statusClasses: Record<StatusVariant, string> = {
	success: "bg-status-success-bg text-status-success-text border-status-success-border",
	warning: "bg-status-warning-bg text-status-warning-text border-status-warning-border",
	error: "bg-status-error-bg text-status-error-text border-status-error-border",
	info: "bg-status-info-bg text-status-info-text border-status-info-border",
};

/**
 * Shared Tailwind class string for inline status badges.
 * Compose with statusClasses[variant] via cn() to apply variant colors.
 */
export const badgeBase =
	"inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap";

// ---------------------------------------------------------------------------
// Priority / Severity Mapping
// ---------------------------------------------------------------------------

export type Priority = "critical" | "high" | "medium" | "low";

export const priorityToStatus: Record<Priority, StatusVariant> = {
	critical: "error",
	high: "warning",
	medium: "info",
	low: "success",
};

export function getPriorityClasses(priority: Priority): string {
	return statusClasses[priorityToStatus[priority]];
}

// ---------------------------------------------------------------------------
// Trend Indicator
// ---------------------------------------------------------------------------

export function getTrendClasses(value: number): string {
	return value >= 0 ? "text-positive" : "text-negative";
}

export function getTrendIcon(value: number): "up" | "down" {
	return value >= 0 ? "up" : "down";
}

export const heatmapRgb = {
	light: [22, 92, 58] as const,
	dark: [37, 99, 235] as const,
} as const;
